import React, { useCallback, useContext, useEffect, useState } from 'react';
import { applyAuthTokensFromResponse } from '../../utils/authTokenStore';
import { mfaService } from '../shared/hooks/AuthService';
import AuthContext from '../shared/AuthContext';
import MfaVerifyStep from '../auth/MfaVerifyStep';
import './SecuritySettingsPanel.css';

/**
 * Password sign-in sessions with MFA enabled must confirm TOTP after login
 * (Google sign-in is exempt on the server).
 */
export default function MfaStepUpGate() {
  const { isAuthenticated, isLoading, logout } = useContext(AuthContext);
  const [stepUpRequired, setStepUpRequired] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!isAuthenticated || isLoading) return;
    setChecking(true);
    try {
      const res = await mfaService.status();
      setStepUpRequired(!!res.data?.stepUpRequired);
    } catch {
      setStepUpRequired(false);
    } finally {
      setChecking(false);
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleVerify = async (code) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await mfaService.stepUp(code);
      applyAuthTokensFromResponse(res.data);
      setStepUpRequired(false);
      window.dispatchEvent(new Event('qnaread:mfa-step-up-complete'));
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || isLoading || checking || !stepUpRequired) {
    return null;
  }

  return (
    <div className="mfa-step-up-overlay" role="dialog" aria-modal="true" aria-labelledby="mfa-step-up-title">
      <div className="mfa-step-up-card">
        <h2 id="mfa-step-up-title">Confirm it&apos;s you</h2>
        <p className="security-muted">
          Two-factor authentication is on for your account. Enter the 6-digit code from your authenticator app
          to continue this session.
        </p>
        <MfaVerifyStep
          onVerify={handleVerify}
          onCancel={() => logout()}
          loading={submitting}
          error={error}
          cancelLabel="Sign out"
        />
      </div>
    </div>
  );
}
